import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredString } from "common";
import React from "react";
import { useMutation } from "react-query";
import { EmvDevice, EmvDeviceType } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";

const validationSchema = object().shape({
    label: requiredString(),
    registrationCode: requiredString(),
});

export interface AddDeviceModalProps {
    isOpen?: boolean;
    device?: EmvDevice;
    onClose: () => void;
}

const initialValues = { label: "", registrationCode: "", type: EmvDeviceType.VERIFONE };

export const AddDeviceModal = ({ isOpen = false, device = initialValues, onClose }: AddDeviceModalProps) => {
    return (
        <Modal shouldCloseOnOutsideClick isOpen={isOpen} size="medium" onClose={onClose}>
            <ModalContent device={device} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    device: EmvDevice | undefined;
    onClose: () => void;
}

const ModalContent = ({ device, onClose }: ModalContentProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const computer = JSON.parse(localStorage.getItem("computer") ?? "{}");

    const { mutateAsync: saveDevice, isLoading } = useMutation(
        async (values: EmvDevice) => {
            if (values.id) {
                return api.patch(`/users/${seller.id}/preferences/stripeTerminal/paymentDevices/${values.id}`, values);
            }

            return api.post(`/users/${seller.id}/preferences/stripeTerminal/paymentDevices`, values);
        },
        {
            onError: () => {
                flash.danger(`Device could not be ${isAddingNewDevice ? "added" : "saved"}.`);
            },
        },
    );

    const handleSubmit = async (values: EmvDevice) => {
        await saveDevice({ computer, ...values });
        onClose();

        invalidate("sellers", seller.id);
        invalidate("users", seller.id);
        invalidate("paymentDevices");
    };

    const isAddingNewDevice = device?.label.length === 0;

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={device!}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit }) => (
                <>
                    <Modal.Header>{isAddingNewDevice ? "Add" : "Edit"} Device</Modal.Header>

                    <Modal.Body>
                        <FormField name="label" label="Label">
                            <InputField placeholder="Name" />
                        </FormField>
                        <FormField name="registrationCode" label="Registration Code">
                            <InputField placeholder="Code from your EMV Device" disabled={!isAddingNewDevice} />
                            {isAddingNewDevice && (
                                <a
                                    href="https://help.xola.com/hc/en-us/articles/4407109372315-Chip-Pin-Contactless-Payments-Desktop-Hardware-Setup-BBPOS-WisePOS-E-"
                                    target="_blank"
                                    className="text-sm font-semibold underline"
                                    rel="noreferrer"
                                >
                                    How to find the registration code?
                                </a>
                            )}
                        </FormField>
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton isLoading={isLoading} color="primary" onClick={handleSubmit}>
                            {isAddingNewDevice ? "Add Device" : "Save Device"}
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
